import { useState } from "react";
import GameShell from "../GameShell";

/**
 * Termômetro Mágico — MASC-2 / CDI self-report.
 * 8 prompts about feelings. Child picks intensity 1–5 with emoji scale.
 * Returns anxiety_score and depression_score (sums of subscales).
 */
export default function MagicThermometer() {
  return (
    <GameShell gameId="magic_thermometer" title="Termômetro Mágico">
      {({ onFinish }) => <Game onFinish={onFinish} />}
    </GameShell>
  );
}

const SCALE = [
  { v: 1, emoji: "😄", label: "Nunca" },
  { v: 2, emoji: "🙂", label: "Quase nunca" },
  { v: 3, emoji: "😐", label: "Às vezes" },
  { v: 4, emoji: "😟", label: "Muitas vezes" },
  { v: 5, emoji: "😢", label: "Sempre" },
];

const PROMPTS = [
  { text: "Eu fico nervoso(a) antes da escola.", dim: "anxiety" },
  { text: "Sinto medo de coisas que outros não sentem.", dim: "anxiety" },
  { text: "Tenho dor de barriga quando estou preocupado(a).", dim: "anxiety" },
  { text: "Fico com medo de falar na frente da turma.", dim: "anxiety" },
  { text: "Sinto vontade de chorar sem motivo.", dim: "depression" },
  { text: "Acho difícil curtir as coisas que gostava antes.", dim: "depression" },
  { text: "Sinto que sou um(a) mau(má) amigo(a).", dim: "depression" },
  { text: "Durmo mal e fico cansado(a).", dim: "depression" },
];

function Game({ onFinish }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // [{prompt, dim, value}]
  const [started] = useState(() => performance.now());

  const choose = (v) => {
    const next = [...answers, { ...PROMPTS[idx], value: v }];
    setAnswers(next);
    if (idx + 1 >= PROMPTS.length) {
      const anxiety = next.filter((a) => a.dim === "anxiety").reduce((s, a) => s + a.value, 0);
      const depression = next
        .filter((a) => a.dim === "depression")
        .reduce((s, a) => s + a.value, 0);
      const elapsed = performance.now() - started;
      // Positive score in game terms: completion-based, not symptom-graded
      const score = 80; // participation reward
      onFinish({
        score,
        duration_ms: Math.round(elapsed),
        metrics: {
          anxiety_score: anxiety,
          depression_score: depression,
          anxiety_max: PROMPTS.filter((p) => p.dim === "anxiety").length * 5,
          depression_max: PROMPTS.filter((p) => p.dim === "depression").length * 5,
          items: PROMPTS.length,
        },
      });
    } else {
      setIdx(idx + 1);
    }
  };

  const p = PROMPTS[idx];

  return (
    <div className="mt-4 max-w-2xl mx-auto" data-testid="thermometer-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">Não tem certo nem errado. Só conte como você se sente.</div>
        <div className="text-xs mt-1 opacity-80">Pergunta {idx + 1} / {PROMPTS.length}</div>
      </div>
      <div className="kid-card pop-in" key={idx}>
        <div className="text-center text-7xl mb-4">🌡️</div>
        <div className="text-xl text-slate-800 text-center font-bold leading-snug mb-6">
          {p.text}
        </div>
        <div className="grid grid-cols-5 gap-2">
          {SCALE.map((s) => (
            <button
              key={s.v}
              onClick={() => choose(s.v)}
              className="flex flex-col items-center bg-sky-50 hover:bg-sky-100 active:scale-95 transition rounded-2xl py-3 px-2 border-2 border-transparent hover:border-sky-300"
              data-testid={`therm-val-${s.v}`}
            >
              <div className="text-3xl">{s.emoji}</div>
              <div className="text-[10px] mt-1 text-slate-600 text-center leading-tight">{s.label}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
